import type { KataScoringDefinition, ScoringCriterion } from './types'

function c(
  id: string,
  text: string,
  suggested: ScoringCriterion['suggested'],
  hint?: string,
): ScoringCriterion {
  return hint ? { id, text, suggested, hint } : { id, text, suggested }
}

const openingPositioning: ScoringCriterion[] = [
  c(
    'itsutsu-gen-bow-side',
    'At bowing, Tori is on the right of the judges and Uke on the left.',
    'forgotten',
  ),
  c(
    'itsutsu-gen-uke-advance',
    'Movement: Following the opening of the kata after bowing, Uke advances in ayumi-ashi to the center starting with his left foot to take shizen-hontai.',
    'medium',
  ),
  c(
    'itsutsu-gen-tori-approach',
    'Tori walks towards Uke slowly in ayumi-ashi starting with his left foot and slowly lifting his right forearm the palm of his right hand forward.',
    'medium',
  ),
  c(
    'itsutsu-gen-toe-position',
    'Tori stands slightly to the right of Uke, his right small toe beside Uke’s right small toe and right shoulder almost touching Uke’s shoulder.',
    'small_medium',
  ),
  c(
    'itsutsu-gen-palm-chest',
    'Tori puts the palm of his right hand, fingers extended on the center of Uke’s chest.',
    'small_medium',
  ),
]

/** Practice judging: 6 scored elements (max 60); opening/closing bows are catalog-only. */
export const itsutsuNoKataScoring: KataScoringDefinition = {
  kataId: 'itsutsu-no-kata',
  maxPerJudge: 60,
  maxPanelOfThree: 180,
  elements: [
    {
      id: 'itsutsu-general',
      kind: 'ceremony',
      name: 'General elements',
      nameJa: '一般観察事項',
      criteria: openingPositioning,
    },
    {
      id: 'itsutsu-form-1',
      kind: 'technique',
      name: 'Form 1',
      nameJa: '一本目',
      techniqueId: 'itsutsu-form-1',
      groupId: 'forms',
      criteria: [
        c(
          'itsutsu-form-1-1',
          'Tori pushes Uke backwards with his right hand, alternating the thrust of the little finger and the thumb. Starting with the little finger.',
          'small_medium',
        ),
        c(
          'itsutsu-form-1-2',
          'Uke steps back taking small steps starting with the left foot.',
          'small_medium',
        ),
        c(
          'itsutsu-form-1-3',
          'Tori steps forward with his right foot then left while pushing Uke’s chest with his right hand.',
          'medium',
        ),
        c(
          'itsutsu-form-1-4',
          'Tori steps forward with his right foot and forcefully pushes Uke to make him lose his balance and fall.',
          'small_medium',
        ),
        c(
          'itsutsu-form-1-5',
          'Uke falls on his back, keeping his feet on the ground and arching his back (feet on the floor about shoulder width apart).',
          'medium',
        ),
      ],
    },
    {
      id: 'itsutsu-form-2',
      kind: 'technique',
      name: 'Form 2',
      nameJa: '二本目',
      techniqueId: 'itsutsu-form-2',
      groupId: 'forms',
      criteria: [
        c(
          'itsutsu-form-2-1',
          'Following Ippon-me, Tori steps back with his right foot and positions himself in shizen-hontai.',
          'small_medium',
        ),
        c(
          'itsutsu-form-2-2',
          'Uke gets up by putting his right knee on the ground and the left knee raised.',
          'medium',
        ),
        c(
          'itsutsu-form-2-3',
          'Uke stands up, right hand as te-gatana at the hip, fingers pointing at Tori, thumb facing upward.',
          'small_medium',
        ),
        c(
          'itsutsu-form-2-4',
          'Uke attacks the center of Tori’s abdomen with his right hand, taking a big step with his right foot.',
          'medium',
        ),
        c(
          'itsutsu-form-2-5',
          'Tori steps back with his left foot and pivots to his left, grabs Uke’s right wrist with his left hand and the inside of Uke’s elbow with his right hand (thumb facing upward).',
          'small_medium',
        ),
        c(
          'itsutsu-form-2-6',
          'Tori with both hands pulls Uke down by dropping his left knee to the ground.',
          'medium',
        ),
        c('itsutsu-form-2-7', 'Uke does a lateral breakfall going forward.', 'medium'),
      ],
    },
    {
      id: 'itsutsu-form-3',
      kind: 'technique',
      name: 'Form 3',
      nameJa: '三本目',
      techniqueId: 'itsutsu-form-3',
      groupId: 'forms',
      criteria: [
        c(
          'itsutsu-form-3-1',
          'Tori and Uke with their left knee on the ground, stretch out their arms like a phoenix, lean slightly forward to stand up (palms facing forward and fingers naturally extended).',
          'medium',
        ),
        c(
          'itsutsu-form-3-2',
          'Tori and Uke turn counterclockwise, to get closer to each other.',
          'big',
        ),
        c(
          'itsutsu-form-3-3',
          'Tori and Uke face each other in the middle, Shomen is to Tori’s left side.',
          'medium',
        ),
        c('itsutsu-form-3-4', 'Tori and Uke cross their arms, left arms on top.', 'medium'),
        c(
          'itsutsu-form-3-5',
          'Tori and Uke raise their right arms and push their left arms down.',
          'medium',
        ),
        c(
          'itsutsu-form-3-6',
          'Tori and Uke turn counterclockwise with gradual acceleration.',
          'medium',
        ),
        c(
          'itsutsu-form-3-7',
          'Tori slides to the ground while lying on his back (front right corner). Tori slides both feet outside of Uke’s right foot to throw Uke over him to the front right side towards the Shomen.',
          'medium',
        ),
        c(
          'itsutsu-form-3-8',
          'Uke does a Ukemi over Tori’s body diagonally and stands in shizen-hontai.',
          'medium',
        ),
      ],
    },
    {
      id: 'itsutsu-form-4',
      kind: 'technique',
      name: 'Form 4',
      nameJa: '四本目',
      techniqueId: 'itsutsu-form-4',
      groupId: 'forms',
      criteria: [
        c('itsutsu-form-4-1', 'Tori stands in shizen-hontai facing Uke at 7m.', 'small_medium'),
        c(
          'itsutsu-form-4-2',
          'Tori steps back with his left foot and lowers his center of gravity by positioning his arms from the front to his left and back side.',
          'medium',
        ),
        c('itsutsu-form-4-3', 'Tori swings back both of his arms forward.', 'small_medium'),
        c(
          'itsutsu-form-4-4',
          'Tori moves towards Uke in ayumi-ashi while gradually speeding up.',
          'small_medium',
        ),
        c(
          'itsutsu-form-4-5',
          'Tori exceeds Uke’s position by about a meter and raising his arms (palms facing forward) stands on his toes, heels raised from tatami.',
          'small_medium',
        ),
        c(
          'itsutsu-form-4-6',
          'Tori (after taking a deep breath) stretches his arms out to the sides (palms facing down) placing his heels on the ground.',
          'medium',
        ),
        c(
          'itsutsu-form-4-7',
          'Tori steps back (2-3 steps) starting with his right foot and places his right elbow on Uke’s chest to break his balance.',
          'medium',
        ),
        c('itsutsu-form-4-8', 'Uke retreats slowly gradually accelerating.', 'small_medium'),
        c(
          'itsutsu-form-4-9',
          'Uke’s being unable to sustain his balance, Tori puts his left knee on the ground and pushes Uke on his back (Ukemi).',
          'medium',
        ),
        c(
          'itsutsu-form-4-10',
          'Uke following the imbalance takes a few steps before falling backwards.',
          'small_medium',
        ),
      ],
    },
    {
      id: 'itsutsu-form-5',
      kind: 'technique',
      name: 'Form 5',
      nameJa: '五本目',
      techniqueId: 'itsutsu-form-5',
      groupId: 'forms',
      criteria: [
        c(
          'itsutsu-form-5-1',
          'Tori on the left of Shomen and Uke on the right are about 8m away.',
          'small_medium',
        ),
        c(
          'itsutsu-form-5-2',
          'Tori and Uke stand in shizen-hontai, then step forward with their right foot in jigo-tai, their arms outstretched.',
          'medium',
        ),
        c(
          'itsutsu-form-5-3',
          'Tori and Uke pivot on their left foot to face each other.',
          'small_medium',
        ),
        c(
          'itsutsu-form-5-4',
          'After taking a deep breath, Tori and Uke, starting with the right foot, run towards each other gradually accelerating.',
          'medium',
        ),
        c(
          'itsutsu-form-5-5',
          'Prior to the collision, Tori slides to the ground on his left side perpendicular to Shomen (by sliding the left foot, then the right to the outside of Uke’s right foot) with both hands on his lower abdomen.',
          'medium',
        ),
        c(
          'itsutsu-form-5-6',
          'Uke does a ukemi over Tori’s body towards the left side of Shomen and stands on the tatami.',
          'medium',
        ),
      ],
    },
  ],
}
